<div class="flex flex-col p-6 gap-6">
  <ep-breadcrumbs>
    <ep-breadcrumb [routerLink]="['/']"><span>Realm</span></ep-breadcrumb>
    <ep-breadcrumb [routerLink]="['/clients']"><span>Clients</span></ep-breadcrumb>
    <ep-breadcrumb
      ><span>{{ client?.clientId || 'Id' }}</span></ep-breadcrumb
    >
  </ep-breadcrumbs>

  @if (isLoading && !client) {
    <div class="flex justify-center gap-3 items-center">
      <div class="lds-dual-ring !h-[1em] !w-[1em] text-[1.5rem]"></div>
      <span>Loading client data...</span>
    </div>
  }

  @if (client) {
    <h1>{{ client.name || client.clientId }}</h1>

    @if (isLoading) {
      <div class="flex justify-center gap-3 items-center">
        <div class="lds-dual-ring !h-[1em] !w-[1em] text-[1.5rem]"></div>
        <span>Refreshing client data...</span>
      </div>
    }

    <section class="flex flex-col gap-7">
      <div class="flex items-center gap-3">
        <h2 class="flex-1">Client Configuration</h2>
        <ep-button size="small" (click)="loadClientData()" [disabled]="isSaving || isLoading">
          <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
            <path
              d="M480-160q-134 0-227-93t-93-227q0-134 93-227t227-93q69 0 132 28.5T720-690v-110h80v280H520v-80h168q-32-56-87.5-88T480-720q-100 0-170 70t-70 170q0 100 70 170t170 70q77 0 139-44t87-116h84q-28 106-114 173t-196 67Z"
            />
          </svg>
          <span>Refresh</span>
        </ep-button>

        <ep-button
          color="primary"
          size="small"
          (click)="saveClientAttributes()"
          [disabled]="isSaving || !me()?.permissions?.length"
        >
          <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
            <path
              d="M840-680v480q0 33-23.5 56.5T760-120H200q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h480l160 160Zm-80 34L646-760H200v560h560v-446ZM480-240q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35ZM240-560h360v-160H240v160Zm-40-86v446-560 114Z"
            />
          </svg>
          <span>Save Changes</span>
        </ep-button>
      </div>
      <form
        class="flex flex-col gap-7"
        [formGroup]="clientForm"
        (ngSubmit)="saveClientAttributes()"
      >
        <ep-tab-container [activeTab]="activeTab" (tab-change)="setActiveTab($event)">
          <ep-tab value="general"><span>General</span></ep-tab>
          <ep-tab value="urls"><span>URLs</span></ep-tab>
          <ep-tab value="auth"><span>Authentication</span></ep-tab>
          <ep-tab value="attributes"><span>Attributes</span></ep-tab>
          <ep-tab value="roles"><span>Roles</span></ep-tab>
          @if (client?.serviceAccountsEnabled) {
            <ep-tab value="service-account-roles"><span>Service Account Roles</span></ep-tab>
          }
          <ep-tab value="admins"><span>App Admins</span></ep-tab>
          <ep-tab value="deny-access"><span>Deny Access</span></ep-tab>
        </ep-tab-container>

        <!-- General Tab -->
        @if (activeTab === 'general') {
          <div class="flex flex-col gap-5" formGroupName="general">
            <app-descriptive-sub-header>
              <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                <path
                  d="m428-340 6 29q3 14 13.5 22.5T472-280h16q14 0 24.5-9t13.5-23l6-28q12-5 22.5-10.5T576-364l29 9q13 4 25.5-1t19.5-16l8-14q7-12 5-26t-13-23l-22-19q2-14 2-26t-2-26l22-19q11-9 13-22.5t-5-25.5l-9-15q-7-11-19-16t-25-1l-29 9q-11-8-21.5-13.5T532-620l-6-29q-3-14-13.5-22.5T488-680h-16q-14 0-24.5 9T434-648l-6 28q-12 5-22.5 10.5T384-596l-29-9q-13-4-25.5 1T310-588l-8 14q-7 12-5 26t13 23l22 19q-2 14-2 26t2 26l-22 19q-11 9-13 22.5t5 25.5l9 15q7 11 19 16t25 1l29-9q11 8 21.5 13.5T428-340Zm-4.5-83.5Q400-447 400-480t23.5-56.5Q447-560 480-560t56.5 23.5Q560-513 560-480t-23.5 56.5Q513-400 480-400t-56.5-23.5ZM200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h560v-560H200v560Zm0-560v560-560Z"
                />
              </svg>
              <ng-container header>General Settings</ng-container>
              <ng-container>
                Configure the general settings for this client, including its name, description, and
                whether it is enabled.</ng-container
              >
            </app-descriptive-sub-header>

            <div class="flex flex-col">
              <label>Client ID</label>
              <span>{{ client.clientId }}</span>
            </div>
            <ep-textfield label="Name" formControlName="name" ngDefaultControl variant="inset">
            </ep-textfield>
            <ep-textfield
              label="Description"
              formControlName="description"
              variant="inset"
              textarea
              rows="3"
              ngDefaultControl
            >
            </ep-textfield>
            <app-toggleable-option
              formControlName="enabled"
              [appDisablePointerEvents]="!me()?.permissions?.includes('ia_basics')"
            >
              <svg viewBox="0 -960 960 960" fill="currentColor">
                <path
                  d="M451.5-491.5Q440-503 440-520v-320q0-17 11.5-28.5T480-880q17 0 28.5 11.5T520-840v320q0 17-11.5 28.5T480-480q-17 0-28.5-11.5Zm-112 343q-65.5-28.5-114-77t-77-114Q120-405 120-480q0-61 20-118.5T198-704q11-14 28-13.5t30 13.5q11 11 10 27t-11 30q-27 36-41 79t-14 88q0 117 81.5 198.5T480-200q117 0 198.5-81.5T760-480q0-46-13.5-89.5T704-649q-10-13-11-28.5t10-26.5q12-12 29-12.5t28 12.5q39 48 59.5 105T840-480q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T480-120q-75 0-140.5-28.5Z"
                />
              </svg>
              <span>Enabled</span>
              <p ngProjectAs="description">
                Disabling the client will prevent any interactions with it, effectively deactivating
                it without deleting its configuration.
              </p>
            </app-toggleable-option>
          </div>

          @if (client.protocol === 'openid-connect') {
            <a class="link link--primary" [href]="openIdConfigurationUrl"
              >OpenID Configuration URL</a
            >
          } @else if (client.protocol === 'saml') {
            <a class="link link--primary" [href]="samlConfigurationUrl">SAML Configuration URL</a>
          }
        }

        <!-- URLs Tab -->
        @if (activeTab === 'urls') {
          <div class="flex flex-col gap-6" formGroupName="urls">
            <app-descriptive-sub-header>
              <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                <path
                  d="M318-120q-82 0-140-58t-58-140q0-40 15-76t43-64l105-105q12-12 28.5-12t28.5 12q12 12 12 28t-12 28L234-401q-17 17-25.5 38.5T200-318q0 49 34.5 83.5T318-200q23 0 45-8.5t39-25.5l105-106q12-11 28-11t28 12q12 12 12 28t-12 28L458-178q-28 28-64 43t-76 15Zm50-248q-12-12-12-28.5t12-28.5l167-167q12-12 28.5-12t28.5 12q12 12 12 28.5T592-535L425-368q-12 12-28.5 12T368-368Zm252-29q-12-12-12-28t12-28l106-105q17-17 25-38t8-44q0-50-34-85t-84-35q-23 0-44.5 8.5T558-726L453-620q-12 12-28 12t-28-12q-12-12-12-28.5t12-28.5l105-105q28-28 64-43t76-15q82 0 139.5 58T839-641q0 39-14.5 75T782-502L677-397q-12 12-28.5 12T620-397Z"
                />
              </svg>
              <ng-container header>Client URLs</ng-container>
              <ng-container>
                Configure the various URLs for this client, including redirect URIs, web origins,
                and logout URLs. Properly setting these URLs is crucial for ensuring secure and
                correct operation of your client application, especially in authentication
                flows.</ng-container
              >
            </app-descriptive-sub-header>
            <ep-textfield
              label="Root URL"
              formControlName="rootUrl"
              hint="The base URL of your application, prepended to other relative URLs. E.g writing https://example.com will result in https://example.com/some/path for a redirect URI of /some/path."
              ngDefaultControl
              variant="inset"
            >
            </ep-textfield>
            <div class="field-wrapper">
              <label>Redirect URIs</label>
              <p class="body-xs">
                Allowed redirect URIs when using Authorization Code Flow or similar. Multiple can be
                defined as well as static or relative ones based on the Root URL. Wildcards should
                be avoided wherenever possible
              </p>
              <div class="flex flex-col gap-2">
                @for (control of redirectUrisArray.controls; track control; let i = $index) {
                  <div class="flex items-center gap-2">
                    <ep-textfield
                      [formControl]="control"
                      class="flex-grow"
                      ngDefaultControl
                      variant="inset"
                    >
                    </ep-textfield>
                    <ep-button
                      size="small"
                      (click)="removeRedirectUri(i)"
                      [disabled]="!me()?.permissions?.includes('ia_urls')"
                      >Remove</ep-button
                    >
                  </div>
                }
                <div class="flex">
                  <ep-button
                    size="small"
                    class="self-start"
                    (click)="addRedirectUri()"
                    [disabled]="!me()?.permissions?.includes('ia_urls')"
                  >
                    <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                      <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
                    </svg>
                    <span>Add URI</span></ep-button
                  >
                </div>
              </div>
            </div>
            <div class="field-wrapper">
              <label>Web Origins</label>
              <p class="body-xs">
                Define Web Origins that use Keycloak APIs e.g. to get tokens without CORS issues.
                Use <b>+</b> to include all Redirect URIs
              </p>
              <div class="flex flex-col gap-2">
                @for (control of webOriginsArray.controls; track control; let i = $index) {
                  <div class="flex items-center gap-2">
                    <ep-textfield
                      [formControl]="control"
                      class="flex-grow"
                      ngDefaultControl
                      variant="inset"
                    >
                    </ep-textfield>
                    <ep-button
                      size="small"
                      (click)="removeWebOrigin(i)"
                      [disabled]="!me()?.permissions?.includes('ia_urls')"
                      >Remove</ep-button
                    >
                  </div>
                }
                <div class="flex">
                  <ep-button
                    size="small"
                    class="self-start"
                    (click)="addWebOrigin()"
                    [disabled]="!me()?.permissions?.includes('ia_urls')"
                  >
                    <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                      <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
                    </svg>
                    <span>Add Origin</span></ep-button
                  >
                </div>
              </div>
            </div>
            <!-- Logout URLs -->
            <div class="field-wrapper">
              <label>Logout URLs</label>
              <p class="body-xs">
                Allowed URLs to redirect to after logout. If left empty, all redirect URIs are
                valid. Once one is defined it will be used exclusively.
              </p>
              <div class="flex flex-col gap-2">
                @for (control of logoutUrlsArray.controls; track control; let i = $index) {
                  <div class="flex items-center gap-2">
                    <ep-textfield
                      [formControl]="control"
                      class="flex-grow"
                      ngDefaultControl
                      variant="inset"
                    >
                    </ep-textfield>
                    <ep-button
                      size="small"
                      (click)="removeLogoutUrl(i)"
                      [disabled]="!me()?.permissions?.includes('ia_urls')"
                      >Remove</ep-button
                    >
                  </div>
                }
                <div class="flex">
                  <ep-button
                    size="small"
                    class="self-start"
                    (click)="addLogoutUrl()"
                    [disabled]="!me()?.permissions?.includes('ia_urls')"
                  >
                    <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                      <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
                    </svg>
                    <span>Add URL</span></ep-button
                  >
                </div>
              </div>
            </div>

            <ep-textfield
              label="Home URL"
              formControlName="homeUrl"
              hint="Provides the default URL for when the auth server needs to redirect or link back to the client application. This is typically used after logout to return the user to the application home page if no specific redirect URI is provided."
              ngDefaultControl
              variant="inset"
            >
            </ep-textfield>
            <ep-textfield
              label="Admin URL"
              formControlName="adminUrl"
              hint="URL to which the Keycloak server can send admin-related callbacks, e.g. for pushing revocation notifications. This URL is called by the server and not by the browser."
              ngDefaultControl
              variant="inset"
            >
            </ep-textfield>
          </div>
        }

        <!-- Authentication Tab -->
        @if (activeTab === 'auth') {
          <div class="flex flex-col gap-8">
            <section class="flex flex-col gap-5" formGroupName="auth">
              <app-descriptive-sub-header>
                <svg slot="svg" class="icon" viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M433-80q-27 0-46.5-18T363-142l-9-66q-13-5-24.5-12T307-235l-62 26q-25 11-50 2t-39-32l-47-82q-14-23-8-49t27-43l53-40q-1-7-1-13.5v-27q0-6.5 1-13.5l-53-40q-21-17-27-43t8-49l47-82q14-23 39-32t50 2l62 26q11-8 23-15t24-12l9-66q4-26 23.5-44t46.5-18h94q27 0 46.5 18t23.5 44l9 66q13 5 24.5 12t22.5 15l62-26q25-11 50-2t39 32l47 82q14 23 8 49t-27 43l-53 40q1 7 1 13.5v27q0 6.5-2 13.5l53 40q21 17 27 43t-8 49l-48 82q-14 23-39 32t-50-2l-60-26q-11 8-23 15t-24 12l-9 66q-4 26-23.5 44T527-80h-94Zm7-80h79l14-106q31-8 57.5-23.5T639-327l99 41 39-68-86-65q5-14 7-29.5t2-31.5q0-16-2-31.5t-7-29.5l86-65-39-68-99 42q-22-23-48.5-38.5T533-694l-13-106h-79l-14 106q-31 8-57.5 23.5T321-633l-99-41-39 68 86 64q-5 15-7 30t-2 32q0 16 2 31t7 30l-86 65 39 68 99-42q22 23 48.5 38.5T427-266l13 106Zm42-180q58 0 99-41t41-99q0-58-41-99t-99-41q-59 0-99.5 41T342-480q0 58 40.5 99t99.5 41Zm-2-140Z"
                  />
                </svg>
                <ng-container header>Authentication Settings</ng-container>
                <ng-container>
                  Configure how this client handles authentication, including required credentials.
                  Adjust these settings to ensure the appropriate level of security and user
                  experience for your application.
                </ng-container>
              </app-descriptive-sub-header>
              <app-toggleable-option
                formControlName="publicClient"
                [appDisablePointerEvents]="!me()?.permissions?.includes('ia_basics')"
              >
                <svg viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M240-80q-33 0-56.5-23.5T160-160v-400q0-33 23.5-56.5T240-640h360v-80q0-50-35-85t-85-35q-42 0-73.5 25.5T364-751q-4 14-16.5 22.5T320-720q-17 0-28.5-11t-8.5-26q14-69 69-116t128-47q83 0 141.5 58.5T680-720v80h40q33 0 56.5 23.5T800-560v400q0 33-23.5 56.5T720-80H240Zm0-80h480v-400H240v400Zm296.5-143.5Q560-327 560-360t-23.5-56.5Q513-440 480-440t-56.5 23.5Q400-393 400-360t23.5 56.5Q447-280 480-280t56.5-23.5ZM240-160v-400 400Z"
                  />
                </svg>
                <span>Public Client</span>
                <p ngProjectAs="description">
                  If enabled, the client is considered a public client and does not require client
                  authentication (e.g., client secrets). Disabling this activates Client
                  Authentication e.g. Secrets once the change is saved.
                </p>
              </app-toggleable-option>
              @if (
                !client.publicClient &&
                authGroup.get('clientAuthenticatorType')?.value === 'client-secret'
              ) {
                <p class="body-xs">
                  Client Authentication is required when using a Client Secret and has to be passed
                  even when using a user facing flow like Authorization Code Flow
                </p>
                <ep-select
                  formControlName="clientAuthenticatorType"
                  ngDefaultControl
                  variant="inset"
                  [appDisablePointerEvents]="!me()?.permissions?.includes('ia_basics')"
                >
                  <label slot="label">Client Authentication</label>
                  <ep-option value="client-secret">Client Secret</ep-option>
                  <ep-option value="client-jwt">Client JWT</ep-option>
                </ep-select>
                <div class="field-wrapper">
                  <label>Client Secret</label>
                  <div class="flex items-center gap-2">
                    <ep-textfield
                      #clientSecretInput
                      [value]="client.secret || ''"
                      class="flex-grow"
                      [inputType]="showClientSecret() ? 'text' : 'password'"
                      [readonly]="true"
                      ngDefaultControl
                      variant="inset"
                    >
                      <ep-button
                        size="small"
                        slot="suffix"
                        (click)="toggleClientSecretVisibility()"
                        type="quiet"
                        [disabled]="!me()?.permissions?.includes('ia_basics')"
                      >
                        @if (showClientSecret()) {
                          <svg
                            class="icon icon--small"
                            viewBox="0 -960 960 960"
                            fill="currentColor"
                          >
                            <path
                              d="m644-428-58-58q9-47-27-88t-93-32l-58-58q17-8 34.5-12t37.5-4q75 0 127.5 52.5T660-500q0 20-4 37.5T644-428Zm128 126-58-56q38-29 67.5-63.5T832-500q-50-101-143.5-160.5T480-720q-29 0-57 4t-55 12l-62-62q41-17 84-25.5t90-8.5q151 0 269 83.5T920-500q-23 59-60.5 109.5T772-302Zm20 246L624-222q-35 11-70.5 16.5T480-200q-151 0-269-83.5T40-500q21-53 53-98.5t73-81.5L56-792l56-56 736 736-56 56ZM222-624q-29 26-53 57t-41 67q50 101 143.5 160.5T480-280q20 0 39-2.5t39-5.5l-36-38q-11 3-21 4.5t-21 1.5q-75 0-127.5-52.5T300-500q0-11 1.5-21t4.5-21l-84-82Zm319 93Zm-151 75Z"
                            />
                          </svg>
                        } @else {
                          <svg
                            class="icon icon--small"
                            viewBox="0 -960 960 960"
                            fill="currentColor"
                          >
                            <path
                              d="M480-320q75 0 127.5-52.5T660-500q0-75-52.5-127.5T480-680q-75 0-127.5 52.5T300-500q0 75 52.5 127.5T480-320Zm0-72q-45 0-76.5-31.5T372-500q0-45 31.5-76.5T480-608q45 0 76.5 31.5T588-500q0 45-31.5 76.5T480-392Zm0 192q-146 0-266-81.5T40-500q54-137 174-218.5T480-800q146 0 266 81.5T920-500q-54 137-174 218.5T480-200Zm0-300Zm0 220q113 0 207.5-59.5T832-500q-50-101-144.5-160.5T480-720q-113 0-207.5 59.5T128-500q50 101 144.5 160.5T480-280Z"
                            />
                          </svg>
                        }
                      </ep-button>
                    </ep-textfield>
                    <ep-button size="small" [disabled]="!me()?.permissions?.includes('ia_basics')"
                      >Regenerate</ep-button
                    >
                  </div>
                </div>
              }
              <app-toggleable-option
                formControlName="consentRequired"
                [appDisablePointerEvents]="!me()?.permissions?.includes('ia_basics')"
              >
                <svg viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M480-40q-112 0-206-51T120-227v67q0 17-11.5 28.5T80-120q-17 0-28.5-11.5T40-160v-160q0-17 11.5-28.5T80-360h160q17 0 28.5 11.5T280-320q0 17-11.5 28.5T240-280h-59q48 72 126.5 116T480-120q141 0 242.5-94T838-445q2-16 14-25.5t28-9.5q17 0 29 10.5t10 25.5q-7 85-44 158.5t-96 128q-59 54.5-135.5 86T480-40Zm0-800q-141 0-242.5 94T122-515q-2 16-14 25.5T80-480q-17 0-29-10.5T41-516q7-85 44-158.5t96-128q59-54.5 135.5-86T480-920q112 0 206 51t154 136v-67q0-17 11.5-28.5T880-840q17 0 28.5 11.5T920-800v160q0 17-11.5 28.5T880-600H720q-17 0-28.5-11.5T680-640q0-17 11.5-28.5T720-680h59q-48-72-126.5-116T480-840Zm35.5 585.5Q530-269 530-290t-14.5-36Q501-341 480-341t-35.5 14.5Q430-312 430-291t14.5 36q14.5 15 35.5 15t35.5-14.5ZM560-490q35-35 46.5-56.5T618-598q0-56-40-89t-98-33q-40 0-71 16.5T358-655q-8 13-2.5 28t19.5 21q14 6 27-1t23-19q10-12 24-19t31-7q27 0 45.5 15.5T544-595q0 17-8 34t-34 40q-25 22-37.5 43T447-430q-2 15 8 26.5t26 11.5q15 0 25-11.5t14-26.5q4-17 13.5-30t26.5-30Z"
                  />
                </svg>
                <span>Consent Required</span>
                <p ngProjectAs="description">
                  If enabled, users will be required to give consent to the client applications
                  requesting access to their data. This is typically used for third-party clients to
                  ensure that users are aware of and approve the permissions being requested by the
                  application.
                </p>
              </app-toggleable-option>
              <app-toggleable-option
                formControlName="frontchannelLogout"
                [appDisablePointerEvents]="!me()?.permissions?.includes('ia_basics')"
              >
                <svg viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h240q17 0 28.5 11.5T480-800q0 17-11.5 28.5T440-760H200v560h240q17 0 28.5 11.5T480-160q0 17-11.5 28.5T440-120H200Zm487-320H400q-17 0-28.5-11.5T360-480q0-17 11.5-28.5T400-520h287l-75-75q-11-11-11-27t11-28q11-12 28-12.5t29 11.5l143 143q12 12 12 28t-12 28L669-309q-12 12-28.5 11.5T612-310q-11-12-10.5-28.5T613-366l74-74Z"
                  />
                </svg>
                <span>Front Channel Logout</span>
                <p ngProjectAs="description">
                  If enabled, the client will be logged out through the browser using the defined
                  Logout URLs. This is typically used for web applications to ensure the user is
                  properly logged out of the application when they log out of Keycloak.
                </p>
              </app-toggleable-option>
            </section>

            <section class="flex flex-col gap-5" formGroupName="audienceMapper">
              <app-descriptive-sub-header>
                <svg slot="svg" class="icon" viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-82q26-36 45-75t31-83H404q12 44 31 83t45 75Zm-104-16q-18-33-31.5-68.5T322-320H204q29 50 72.5 87t99.5 55Zm208 0q56-18 99.5-55t72.5-87H638q-9 38-22.5 73.5T584-178ZM170-400h136q-3-20-4.5-39.5T300-480q0-21 1.5-40.5T306-560H170q-5 20-7.5 39.5T160-480q0 21 2.5 40.5T170-400Zm216 0h188q3-20 4.5-39.5T580-480q0-21-1.5-40.5T574-560H386q-3 20-4.5 39.5T380-480q0 21 1.5 40.5T386-400Zm268 0h136q5-20 7.5-39.5T800-480q0-21-2.5-40.5T790-560H654q3 20 4.5 39.5T660-480q0 21-1.5 40.5T654-400Zm-16-240h118q-29-50-72.5-87T584-782q18 33 31.5 68.5T638-640Zm-234 0h152q-12-44-31-83t-45-75q-26 36-45 75t-31 83Zm-208 0h118q9-38 22.5-73.5T376-782q-56 18-99.5 55T204-640Z"
                  />
                </svg>
                <ng-container header>Token Audience</ng-container>
                <ng-container>
                  Control whether this client's own client ID is added to the audience (aud) claim
                  of access tokens issued for this client.
                </ng-container>
              </app-descriptive-sub-header>
              <app-toggleable-option formControlName="enabled">
                <svg viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z"
                  />
                </svg>
                <span>Include Self as Audience</span>
                <p ngProjectAs="description">
                  Adds this client's own client ID to the token audience (aud) claim, alongside any
                  other audiences already present, so resource servers that expect the audience to
                  include their own client ID can validate tokens issued by this client. Applies to
                  access tokens only.
                </p>
              </app-toggleable-option>
            </section>

            <section class="flex flex-col gap-5" formGroupName="flows">
              <app-descriptive-sub-header>
                <svg slot="svg" class="icon" viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M600-160v-80H440v-200h-80v80H80v-240h280v80h80v-200h160v-80h280v240H600v-80h-80v320h80v-80h280v240H600Zm80-80h120v-80H680v80ZM160-440h120v-80H160v80Zm520-200h120v-80H680v80Zm0 400v-80 80ZM280-440v-80 80Zm400-200v-80 80Z"
                  />
                </svg>
                <ng-container header>Authentication Flows</ng-container>
                <ng-container>
                  Configure the authentication flows supported by this client. Choose the
                  appropriate flows based on your application's architecture and security
                  requirements.
                </ng-container>
              </app-descriptive-sub-header>
              <app-toggleable-option
                formControlName="standardFlowEnabled"
                [appDisablePointerEvents]="!me()?.permissions?.includes('ia_basics')"
              >
                <svg viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M480-92q-10 0-20-2.5t-19-7.5L161-257q-19-11-30-29.5T120-327v-306q0-22 11-40.5t30-29.5l280-155q9-5 19-7.5t20-2.5q10 0 20 2.5t19 7.5l280 155q19 11 30 29.5t11 40.5v306q0 22-11 40.5T799-257L519-102q-9 5-19 7.5T480-92ZM364-590q22-23 51.5-36.5T480-640q35 0 64.5 13.5T596-590l120-67-236-131-236 131 120 67Zm76 396v-131q-52-14-86-56.5T320-480q0-11 1-21t4-19l-125-70v263l240 133Zm40-206q33 0 56.5-23.5T560-480q0-33-23.5-56.5T480-560q-33 0-56.5 23.5T400-480q0 33 23.5 56.5T480-400Zm40 206 240-133v-263l-125 70q3 10 4 19.5t1 20.5q0 56-34 98.5T520-325v131Z"
                  />
                </svg>
                <span>Authorization Code (Standard Flow)</span>
                <p ngProjectAs="description">
                  The standard OAuth 2.0 Authorization code flow is the most secure and commonly
                  used flow, suitable for server-side and confidential applications. It involves an
                  initial authorization request that results in an authorization code, which is then
                  exchanged for tokens. This flow ensures that tokens are never exposed to the user
                  agent and is recommended for most applications.
                </p>
              </app-toggleable-option>

              <app-toggleable-option
                formControlName="implicitFlowEnabled"
                [appDisablePointerEvents]="!me()?.permissions?.includes('ia_basics')"
              >
                <svg viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v240q0 17-11.5 28.5T840-440q-17 0-28.5-11.5T800-480v-160H160v400h280q17 0 28.5 11.5T480-200q0 17-11.5 28.5T440-160H160ZM760 0q-57 0-104-28.5T583-105q-7-12-2.5-25.5T598-149q11-5 22 0t17 16q18 33 51 53t72 20q58 0 99-41t41-99q0-58-41-99t-99-41q-28 0-53 10t-45 30h28q13 0 21.5 8.5T720-270q0 13-8.5 21.5T690-240h-90q-17 0-28.5-11.5T560-280v-90q0-13 8.5-21.5T590-400q13 0 21.5 8.5T620-370v27q29-27 65-42t75-15q83 0 141.5 58.5T960-200q0 83-58.5 141.5T760 0Z"
                  />
                </svg>
                <span>Implicit Flow (Deprecated)</span>
                <p ngProjectAs="description">
                  The largely deprecated implicit flow is a simplified flow designed for public
                  clients, such as single-page applications (SPAs), that cannot securely store
                  client secrets. In this flow, tokens are returned directly in the authorization
                  response without an intermediate authorization code exchange. However, due to
                  security concerns, it is recommended to use the Authorization Code Flow with PKCE
                  for SPAs instead of the Implicit Flow.
                </p>
              </app-toggleable-option>
              <app-toggleable-option
                formControlName="directAccessGrantsEnabled"
                [appDisablePointerEvents]="!me()?.permissions?.includes('ia_basics')"
              >
                <svg viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M120-280h720q17 0 28.5 11.5T880-240q0 17-11.5 28.5T840-200H120q-17 0-28.5-11.5T80-240q0-17 11.5-28.5T120-280Zm40-222-19 34q-6 11-18 14t-23-3q-11-6-14-18t3-23l19-34H70q-13 0-21.5-8.5T40-562q0-13 8.5-21.5T70-592h38l-19-32q-6-11-3-23t14-18q11-6 23-3t18 14l19 32 19-32q6-11 18-14t23 3q11 6 14 18t-3 23l-19 32h38q13 0 21.5 8.5T280-562q0 13-8.5 21.5T250-532h-38l19 34q6 11 3 23t-14 18q-11 6-23 3t-18-14l-19-34Zm320 0-19 34q-6 11-18 14t-23-3q-11-6-14-18t3-23l19-34h-38q-13 0-21.5-8.5T360-562q0-13 8.5-21.5T390-592h38l-19-32q-6-11-3-23t14-18q11-6 23-3t18 14l19 32 19-32q6-11 18-14t23 3q11 6 14 18t-3 23l-19 32h38q13 0 21.5 8.5T600-562q0 13-8.5 21.5T570-532h-38l19 34q6 11 3 23t-14 18q-11 6-23 3t-18-14l-19-34Zm320 0-19 34q-6 11-18 14t-23-3q-11-6-14-18t3-23l19-34h-38q-13 0-21.5-8.5T680-562q0-13 8.5-21.5T710-592h38l-19-32q-6-11-3-23t14-18q11-6 23-3t18 14l19 32 19-32q6-11 18-14t23 3q11 6 14 18t-3 23l-19 32h38q13 0 21.5 8.5T920-562q0 13-8.5 21.5T890-532h-38l19 34q6 11 3 23t-14 18q-11 6-23 3t-18-14l-19-34Z"
                  />
                </svg>
                <span>Password Flow (Direct Access Grants)</span>
                <p ngProjectAs="description">
                  The Resource Owner Password Credentials Grant, also known as Direct Access Grants
                  in Keycloak, allows clients to exchange a user's credentials (username and
                  password) directly for an access token. This flow is typically used in highly
                  trusted applications, such as official mobile apps, where the client can securely
                  handle user credentials. However, it is generally not recommended for third-party
                  applications due to security risks associated with handling user credentials
                  directly.
                </p>
              </app-toggleable-option>
              <app-toggleable-option
                formControlName="serviceAccountsEnabled"
                [appDisablePointerEvents]="!me()?.permissions?.includes('ia_basics')"
              >
                <svg viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M280-720v-40q0-33 23.5-56.5T360-840h240q33 0 56.5 23.5T680-760v40h28q23 0 43 13t29 35l94 216q3 8 4.5 16t1.5 16v184q0 33-23.5 56.5T800-160H160q-33 0-56.5-23.5T80-240v-184q0-8 1.5-16t4.5-16l94-216q9-22 29-35t43-13h28Zm80 0h240v-40H360v40Zm-80 240v-1q0-17 11.5-28.5T320-521q17 0 28.5 11.5T360-481v1h240v-1q0-17 11.5-28.5T640-521q17 0 28.5 11.5T680-481v1h96l-68-160H252l-68 160h96Zm0 80H160v160h640v-160H680v1q0 17-11.5 28.5T640-359q-17 0-28.5-11.5T600-399v-1H360v1q0 17-11.5 28.5T320-359q-17 0-28.5-11.5T280-399v-1Zm200-40Zm0-40Zm0 80Z"
                  />
                </svg>
                <span>Service Accounts Enabled</span>
                <p ngProjectAs="description">
                  Service Accounts Enabled (Client Credentials, also enables assigning roles to the
                  client by the admins)
                </p>
              </app-toggleable-option>
              @if (
                clientForm.get('flows.serviceAccountsEnabled')?.value &&
                clientForm.get('auth.publicClient')?.value
              ) {
                <ep-alert color="warning" variant="alt" size="small">
                  <svg viewBox="0 -960 960 960" class="icon" slot="prefix">
                    <path
                      d="m40-120 440-760 440 760H40Zm138-80h604L480-720 178-200Zm302-40q17 0 28.5-11.5T520-280q0-17-11.5-28.5T480-320q-17 0-28.5 11.5T440-280q0 17 11.5 28.5T480-240Zm-40-120h80v-200h-80v200Zm40-100Z"
                    />
                  </svg>
                  <span slot="header">Public Client with Service Accounts won't work</span>
                  <p slot="subtext">
                    Retrieving a service account token requires client authentication, which is not
                    possible while "Public Client" is enabled. Disable "Public Client" above so the
                    client also gets client credentials, otherwise requests for a service account
                    token will fail.
                  </p>
                </ep-alert>
              }
            </section>
          </div>
        }

        <!-- Roles Tab -->
        @if (activeTab === 'roles') {
          <div class="flex flex-col gap-5">
            <div class="flex justify-between items-center">
              <app-descriptive-sub-header>
                <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M287-527q-47-47-47-113t47-113q47-47 113-47t113 47q47 47 47 113t-47 113q-47 47-113 47t-113-47ZM80-240v-32q0-33 17-62t47-44q58-29 120-45.5T391-440q17 0 28.5 12t11.5 29q0 17-11.5 28.5T391-359q-56 0-108.5 14T180-306q-10 5-15 14t-5 20v32h231q17 0 28.5 11.5T431-200q0 17-11.5 28.5T391-160H160q-33 0-56.5-23.5T80-240Zm554 88-6-28q-12-5-22.5-10.5T584-204l-29 9q-13 4-25.5-1T510-212l-8-14q-7-12-5-26t13-23l22-19q-2-14-2-26t2-26l-22-19q-11-9-13-22.5t5-25.5l9-15q7-11 19-16t25-1l29 9q11-8 21.5-13.5T628-460l6-29q3-14 13.5-22.5T672-520h16q14 0 24.5 9t13.5 23l6 28q12 5 22.5 11t21.5 15l27-9q14-5 27 0t20 17l8 14q7 12 5 26t-13 23l-22 19q2 12 2 25t-2 25l22 19q11 9 13 22.5t-5 25.5l-9 15q-7 11-19 16t-25 1l-29-9q-11 8-21.5 13.5T732-180l-6 29q-3 14-13.5 22.5T688-120h-16q-14 0-24.5-9T634-152Zm102.5-111.5Q760-287 760-320t-23.5-56.5Q713-400 680-400t-56.5 23.5Q600-353 600-320t23.5 56.5Q647-240 680-240t56.5-23.5Zm-280-320Q480-607 480-640t-23.5-56.5Q433-720 400-720t-56.5 23.5Q320-673 320-640t23.5 56.5Q367-560 400-560t56.5-23.5ZM400-640Zm12 400Z"
                  />
                </svg>
                <ng-container header>Client Roles</ng-container>
                <ng-container>
                  Manage the roles associated with this client. Roles are used to define permissions
                  and access control for users and applications interacting with this client.
                </ng-container>
              </app-descriptive-sub-header>
              <ep-button
                size="small"
                (click)="openCreateRoleModal()"
                [disabled]="!me()?.permissions?.includes('ia_basics')"
              >
                <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                  <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
                </svg>
                <span>Add Role</span>
              </ep-button>
            </div>

            <!-- Roles Loading State -->
            @if (isLoadingRoles) {
              <div class="text-center py-4">Loading roles...</div>
            }

            <!-- No Roles Message -->
            @if (!isLoadingRoles && clientRoles.length === 0) {
              <div class="text-center py-4 text-gray-500">No roles defined for this client</div>
            }

            <!-- Roles Table -->
            @if (!isLoadingRoles && clientRoles.length > 0) {
              <div class="overflow-x-auto">
                <table class="w-full text--small text-fg-muted text-left">
                  <thead class="body-xs text-fg-subtle">
                    <tr>
                      <th class="px-3 py-2">Name</th>
                      <th class="px-3 py-2">Description</th>
                      <th class="px-3 py-2">Composite</th>
                      <th class="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (role of clientRoles; track role.name) {
                      <tr class="border-t stroke-subtle">
                        <td class="px-3 py-1">{{ role.name }}</td>
                        <td class="px-3 py-1">{{ role.description || '-' }}</td>
                        <td class="px-3 py-1">
                          {{ role.composite ? 'Yes' : 'No' }}
                        </td>
                        <td class="px-3 py-1 text-right whitespace-nowrap">
                          <ep-button
                            type="quiet"
                            size="small"
                            class="mx-1"
                            title="Manage Composites"
                            (click)="openManageCompositesModal(role)"
                            [disabled]="!me()?.permissions?.includes('ia_basics')"
                          >
                            <svg
                              class="icon icon--small"
                              viewBox="0 -960 960 960"
                              fill="currentColor"
                            >
                              <path
                                d="M480-120q-33 0-56.5-23.5T400-200q0-33 23.5-56.5T480-280q33 0 56.5 23.5T560-200q0 33-23.5 56.5T480-120Zm0-320q-33 0-56.5-23.5T400-520q0-33 23.5-56.5T480-600q33 0 56.5 23.5T560-520q0 33-23.5 56.5T480-440Zm0-320q-33 0-56.5-23.5T400-840q0-33 23.5-56.5T480-920q33 0 56.5 23.5T560-840q0 33-23.5 56.5T480-760Z"
                              />
                            </svg>
                          </ep-button>
                          <ep-button
                            type="quiet"
                            size="small"
                            class="mx-1"
                            (click)="openEditRoleModal(role)"
                            [disabled]="!me()?.permissions?.includes('ia_basics')"
                          >
                            <svg
                              class="icon icon--small"
                              viewBox="0 -960 960 960"
                              fill="currentColor"
                            >
                              <path
                                d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z"
                              />
                            </svg>
                          </ep-button>
                          <ep-button
                            type="quiet"
                            size="small"
                            color="danger"
                            class="mx-1"
                            (click)="openDeleteRoleModal(role)"
                            [disabled]="!me()?.permissions?.includes('ia_basics')"
                          >
                            <svg
                              class="icon icon--small"
                              viewBox="0 -960 960 960"
                              fill="currentColor"
                            >
                              <path
                                d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"
                              />
                            </svg>
                          </ep-button>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }

            <div class="divider"></div>

            <div class="flex flex-col gap-3">
              <label>Assign a Role to Another Client's Service Account</label>
              <p class="body-xs text-fg-subtle">
                The target client dropdown only lists clients that have a service account enabled
                <strong>and</strong> where you hold at least the "Users &amp; Roles" permission. If
                the client you're looking for is missing, ask an admin of that client to grant you
                the "Users &amp; Roles" permission there first — you can't assign roles to a service
                account you don't manage.
              </p>
              <div class="grid grid-cols-2 gap-4">
                <ep-combobox
                  dynamic="true"
                  manualDynamic="true"
                  searchDelay="300"
                  placeholder="Search or pick a client..."
                  size="medium"
                  [value]="selectedOtherServiceAccountClient()?.id || ''"
                  (search)="searchOtherServiceAccountClients($event)"
                  (change)="onOtherServiceAccountClientSelected($event)"
                  [disabled]="!me()?.permissions?.includes('ia_users')"
                >
                  <label slot="label">Target client</label>
                  @for (client of otherServiceAccountClientResults(); track client.id) {
                    <ep-option [value]="client.id" [label]="client.name || client.clientId">
                      <span slot="text">{{ client.name || client.clientId }}</span>
                      <span slot="subtext">{{ client.clientId }}</span>
                    </ep-option>
                  }
                </ep-combobox>
                <ep-select
                  placeholder="Select role"
                  size="medium"
                  [value]="selectedRoleToGrant()?.id || ''"
                  (change)="onRoleToGrantSelected($event)"
                  [disabled]="!me()?.permissions?.includes('ia_users')"
                >
                  <label slot="label">Role to grant</label>
                  @for (role of clientRoles; track role.id) {
                    <ep-option [value]="role.id" [label]="role.name">
                      <span slot="text">{{ role.name }}</span>
                    </ep-option>
                  }
                </ep-select>
              </div>
              @if (otherServiceAccountClientResultsTruncated()) {
                <p class="body-xs text-fg-subtle">
                  Only clients you have permission to manage are shown here (up to 20). There may be
                  additional clients in the realm you don't currently have access to.
                </p>
              }
              @if (selectedOtherServiceAccountClient() && selectedRoleToGrant()) {
                <div class="flex items-center gap-3">
                  @if (isCheckingRoleGrantStatus()) {
                    <span class="body-xs text-fg-subtle">Checking...</span>
                  } @else if (roleGrantIsAssigned()) {
                    <span class="body-xs text-fg-subtle">Already assigned</span>
                    <ep-button
                      size="small"
                      color="danger"
                      (click)="removeRoleGrant()"
                      [disabled]="isSavingRoleGrant()"
                    >
                      <span>Remove</span>
                    </ep-button>
                  } @else {
                    <ep-button
                      size="small"
                      color="primary"
                      (click)="assignRoleGrant()"
                      [disabled]="isSavingRoleGrant()"
                    >
                      <span>Assign</span>
                    </ep-button>
                  }
                </div>
              }
            </div>

            <!-- Create Role Modal -->
            <ep-modal [open]="showCreateRoleModal" (close)="closeRoleModal()">
              <h3 slot="header">Create New Role</h3>
              <form [formGroup]="roleForm" (ngSubmit)="createRole()" class="flex flex-col gap-5">
                <ep-textfield
                  label="Role Name"
                  formControlName="name"
                  [required]="true"
                  ngDefaultControl
                  variant="inset"
                >
                  @if (roleForm.get('name')?.invalid && roleForm.get('name')?.touched) {
                    <div slot="error">Role name is required</div>
                  }
                </ep-textfield>
                <ep-textfield
                  label="Description"
                  formControlName="description"
                  variant="inset"
                  textarea
                  rows="3"
                  ngDefaultControl
                >
                </ep-textfield>
                <div class="flex justify-end gap-2" slot="actions">
                  <ep-button (click)="closeRoleModal()">Cancel</ep-button>
                  <ep-button
                    color="primary"
                    type="submit"
                    (click)="createRole()"
                    [disabled]="roleForm.invalid || isSavingRole"
                  >
                    <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                      <path
                        d="M840-680v480q0 33-23.5 56.5T760-120H200q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h480l160 160Zm-80 34L646-760H200v560h560v-446ZM480-240q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35ZM240-560h360v-160H240v160Zm-40-86v446-560 114Z"
                      />
                    </svg>
                    <span>Create</span>
                  </ep-button>
                </div>
              </form>
            </ep-modal>

            <!-- Edit Role Modal -->
            <ep-modal [open]="showEditRoleModal" (close)="closeRoleModal()">
              <h3 slot="header">Edit Role</h3>
              <form [formGroup]="roleForm" (ngSubmit)="updateRole()">
                <ep-textfield
                  label="Role Name"
                  formControlName="name"
                  [readonly]="true"
                  ngDefaultControl
                >
                </ep-textfield>
                <ep-textfield
                  label="Description"
                  formControlName="description"
                  variant="textarea"
                  rows="3"
                  ngDefaultControl
                >
                </ep-textfield>
                <div slot="actions" class="flex justify-end gap-2">
                  <ep-button (click)="closeRoleModal()">Cancel</ep-button>
                  <ep-button
                    color="primary"
                    type="submit"
                    (click)="updateRole()"
                    [disabled]="roleForm.invalid || isSavingRole"
                  >
                    <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                      <path
                        d="M840-680v480q0 33-23.5 56.5T760-120H200q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h480l160 160Zm-80 34L646-760H200v560h560v-446ZM480-240q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35ZM240-560h360v-160H240v160Zm-40-86v446-560 114Z"
                      />
                    </svg>
                    <span>Save</span>
                  </ep-button>
                </div>
              </form>
            </ep-modal>

            <!-- Delete Role Confirmation Modal -->
            <ep-modal [open]="showDeleteRoleModal" (close)="closeRoleModal()">
              <h3 slot="header">Delete Role</h3>
              <p class="my-4">
                Are you sure you want to delete the role "{{ selectedRole?.name }}"?
              </p>
              <div slot="actions" class="flex justify-end gap-2">
                <ep-button (click)="closeRoleModal()">Cancel</ep-button>
                <ep-button color="danger" (click)="deleteRole()" [disabled]="isSavingRole">
                  Delete
                </ep-button>
              </div>
            </ep-modal>

            <!-- Manage Composites Modal -->
            <ep-modal [open]="showManageCompositesModal()" (close)="closeManageCompositesModal()">
              <h3 slot="header">Manage Composites — {{ compositeRoleTarget()?.name }}</h3>
              <div class="flex flex-col gap-4 min-w-[350px]">
                @if (isLoadingComposites()) {
                  <div class="text-center py-4">Loading composite members...</div>
                } @else {
                  @if (compositeMembers().length === 0) {
                    <div class="text-center py-4 text-gray-500">
                      This role has no composite members yet
                    </div>
                  } @else {
                    <ul class="flex flex-col gap-2">
                      @for (member of compositeMembers(); track member.id) {
                        <li
                          class="flex items-center justify-between gap-2 border-b stroke-subtle pb-2"
                        >
                          <span class="body-xs text-fg-subtle">{{ member.clientLabel }}</span>
                          <span class="flex-1">{{ member.name }}</span>
                          <ep-button
                            type="quiet"
                            size="small"
                            color="danger"
                            title="Remove"
                            (click)="removeCompositeMember(member)"
                            [disabled]="isSavingComposite()"
                          >
                            <svg
                              class="icon icon--small"
                              viewBox="0 -960 960 960"
                              fill="currentColor"
                            >
                              <path
                                d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"
                              />
                            </svg>
                          </ep-button>
                        </li>
                      }
                    </ul>
                  }
                }

                <div class="divider"></div>

                <div class="flex flex-col gap-3">
                  <label>Add member role</label>
                  <ep-combobox
                    dynamic="true"
                    manualDynamic="true"
                    searchDelay="300"
                    placeholder="Search or pick a client..."
                    size="medium"
                    [value]="selectedCompositeClientValue()"
                    (search)="searchCompositeClients($event)"
                    (change)="onCompositeClientSelected($event)"
                  >
                    <label slot="label">Select Client</label>
                    @if (isRealmAdmin()) {
                      <ep-option value="REALM" label="Realm roles">
                        <span slot="text">Realm roles</span>
                      </ep-option>
                    }
                    @for (client of compositeClientSearchResults(); track client.id) {
                      <ep-option [value]="client.id" [label]="client.name || client.clientId">
                        <span slot="text">{{ client.name || client.clientId }}</span>
                        <span slot="subtext">{{ client.clientId }}</span>
                      </ep-option>
                    }
                  </ep-combobox>
                  @if (compositeClientResultsTruncated()) {
                    <p class="body-xs text-fg-subtle">
                      Only clients you have permission to manage are shown here (up to 20). There
                      may be additional clients in the realm you don't currently have access to.
                    </p>
                  }
                  <ep-select
                    placeholder="Select role"
                    size="medium"
                    [value]="selectedCompositeRoleToAdd()?.id || ''"
                    (change)="onCompositeRoleToAddSelected($event)"
                  >
                    <label slot="label">Select role</label>
                    @for (candidate of compositeCandidateRoles(); track candidate.id) {
                      <ep-option [value]="candidate.id" [label]="candidate.name">
                        <span slot="text">{{ candidate.name }}</span>
                      </ep-option>
                    }
                  </ep-select>
                </div>
              </div>
              <div class="flex justify-end gap-2" slot="actions">
                <ep-button (click)="closeManageCompositesModal()">Close</ep-button>
                <ep-button
                  color="primary"
                  (click)="addCompositeMember()"
                  [disabled]="!selectedCompositeRoleToAdd() || isSavingComposite()"
                >
                  <span>Add</span>
                </ep-button>
              </div>
            </ep-modal>
          </div>
        }

        <!-- Service Account Roles Tab -->
        @if (activeTab === 'service-account-roles') {
          <div class="flex flex-col gap-5">
            <app-descriptive-sub-header>
              <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                <path
                  d="M480-120q-33 0-56.5-23.5T400-200q0-33 23.5-56.5T480-280q33 0 56.5 23.5T560-200q0 33-23.5 56.5T480-120Zm0-320q-33 0-56.5-23.5T400-520q0-33 23.5-56.5T480-600q33 0 56.5 23.5T560-520q0 33-23.5 56.5T480-440Zm0-320q-33 0-56.5-23.5T400-840q0-33 23.5-56.5T480-920q33 0 56.5 23.5T560-840q0 33-23.5 56.5T480-760Z"
                />
              </svg>
              <ng-container header>Service Account Roles</ng-container>
              <ng-container>
                This client has a service account, used for machine-to-machine (client credentials)
                authentication. Assign client roles or realm roles to grant the service account
                permissions.
              </ng-container>
            </app-descriptive-sub-header>

            <!-- All Assigned Roles (across every client + realm) -->
            <div class="flex flex-col gap-3">
              <h3>All Assigned Roles</h3>
              @if (isLoadingServiceAccountAllRoles()) {
                <div class="text-center py-4">Loading roles...</div>
              } @else if (serviceAccountAllRoleGroups().length === 0) {
                <div class="text-center py-4 text-gray-500">
                  No roles are assigned to this service account yet
                </div>
              } @else {
                <div class="flex flex-col gap-4">
                  @for (group of serviceAccountAllRoleGroups(); track group.roleClientId) {
                    <div class="flex flex-col gap-1">
                      <span class="body-xs text-fg-subtle">{{ group.clientLabel }}</span>
                      <div class="overflow-y-auto max-h-40">
                        @for (role of group.roles; track role.id) {
                          <ep-item
                            ><span>{{ role.name }}</span>
                            @if (role.description) {
                              <span slot="subtext">({{ role.description }})</span>
                            }
                          </ep-item>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>

            <div class="divider"></div>

            <div class="flex flex-col gap-3">
              <label>Role source</label>
              <p class="body-xs text-fg-subtle">
                Only clients where you hold at least the "Users &amp; Roles" permission are listed
                as a role source. If a client you expect to see is missing, ask an admin of that
                client to grant you the "Users &amp; Roles" permission there first.
              </p>
              <ep-combobox
                dynamic="true"
                manualDynamic="true"
                searchDelay="300"
                placeholder="Search or pick a client..."
                size="medium"
                [value]="serviceAccountScopeValue()"
                (search)="searchServiceAccountClients($event)"
                (change)="onServiceAccountScopeSelected($event)"
                [disabled]="!me()?.permissions?.includes('ia_users')"
              >
                <label slot="label">Select Client</label>
                @if (isRealmAdmin()) {
                  <ep-option value="REALM" label="Realm roles">
                    <span slot="text">Realm roles</span>
                  </ep-option>
                }
                @for (client of serviceAccountClientSearchResults(); track client.id) {
                  <ep-option [value]="client.id" [label]="client.name || client.clientId">
                    <span slot="text">{{ client.name || client.clientId }}</span>
                    <span slot="subtext">{{ client.clientId }}</span>
                  </ep-option>
                }
              </ep-combobox>
              @if (serviceAccountClientResultsTruncated()) {
                <p class="body-xs text-fg-subtle">
                  Only clients you have permission to manage are shown here (up to 20). There may be
                  additional clients in the realm you don't currently have access to.
                </p>
              }
            </div>

            @if (isLoadingServiceAccountRoles()) {
              <div class="text-center py-4">Loading roles...</div>
            }

            @if (!isLoadingServiceAccountRoles() && serviceAccountScope()) {
              <div class="grid grid-cols-2 gap-6">
                <!-- Available roles -->
                <div class="flex flex-col gap-3">
                  <h3>Available Roles</h3>
                  @if (serviceAccountAvailableRoles().length === 0) {
                    <div class="text-center py-4 text-gray-500">No available roles</div>
                  } @else {
                    <ep-select
                      placeholder="Select role"
                      size="medium"
                      [value]="selectedServiceAccountRoleToAdd()?.id || ''"
                      (change)="onServiceAccountRoleToAddSelected($event)"
                      [disabled]="!me()?.permissions?.includes('ia_users')"
                    >
                      <label slot="label">Select role</label>
                      @for (role of serviceAccountAvailableRoles(); track role.id) {
                        <ep-option [value]="role.id" [label]="role.name">
                          <span slot="text">{{ role.name }}</span>
                        </ep-option>
                      }
                    </ep-select>
                    <ep-button
                      color="primary"
                      size="small"
                      (click)="assignServiceAccountRole()"
                      [disabled]="
                        !selectedServiceAccountRoleToAdd() ||
                        isSavingServiceAccountRole() ||
                        !me()?.permissions?.includes('ia_users')
                      "
                    >
                      <span>Add</span>
                    </ep-button>
                  }
                </div>

                <!-- Assigned roles -->
                <div class="flex flex-col gap-3">
                  <h3>Assigned Roles</h3>
                  @if (serviceAccountAssignedRoles().length === 0) {
                    <div class="text-center py-4 text-gray-500">No roles assigned</div>
                  } @else {
                    <div class="overflow-y-auto max-h-80">
                      @for (role of serviceAccountAssignedRoles(); track role.id) {
                        <ep-item>
                          <span slot="text">{{ role.name }}</span>
                          @if (role.description) {
                            <span slot="subtext">{{ role.description }}</span>
                          }
                          <span slot="extra">
                            <ep-button
                              type="quiet"
                              size="small"
                              color="danger"
                              title="Remove"
                              (click)="removeServiceAccountRole(role)"
                              [disabled]="
                                isSavingServiceAccountRole() ||
                                !me()?.permissions?.includes('ia_users')
                              "
                            >
                              <svg
                                class="icon icon--small"
                                viewBox="0 -960 960 960"
                                fill="currentColor"
                              >
                                <path
                                  d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"
                                />
                              </svg>
                            </ep-button>
                          </span>
                        </ep-item>
                      }
                    </div>
                  }
                </div>
              </div>

              <div>
                <h3 class="mt-6">Effective Roles</h3>
                <div class="overflow-y-auto max-h-80">
                  @if (serviceAccountEffectiveRoles().length === 0) {
                    <div class="text-center py-4 text-gray-500">No effective roles</div>
                  } @else {
                    @for (role of serviceAccountEffectiveRoles(); track role.id) {
                      <ep-item
                        ><span>{{ role.name }}</span>
                        @if (role.description) {
                          <span slot="subtext">({{ role.description }})</span>
                        }
                      </ep-item>
                    }
                  }
                </div>
              </div>
            }
          </div>
        }

        <!-- Custom Attributes Tab -->
        @if (activeTab === 'attributes') {
          <div class="flex flex-col gap-8" formGroupName="attributes">
            <section class="flex flex-col gap-6" formGroupName="salesforce">
              <app-descriptive-sub-header>
                <svg class="icon" viewBox="0 0 520 520" fill="currentColor">
                  <path
                    d="M217 119c17-17 40-28 66-28 34 0 64 19 80 47 14-6 29-10 45-10 62 0 112 50 112 112s-50 112-112 112c-8 0-15-1-22-2a82.4 82.4 0 0 1-72 42c-13 0-25-3-36-8a92.7 92.7 0 0 1-86 56c-40 0-75-25-88-61-6 1-12 2-18 2a87 87 0 0 1-44-162 100.5 100.5 0 0 1 93-140c35 1 64 16 82 40"
                  />
                </svg>
                <ng-container header>Salesforce</ng-container>
                <ng-container>
                  These attributes are used for Salesforce integration and should be set according
                  to Salesforce's requirements.
                </ng-container>
              </app-descriptive-sub-header>
              <ep-textfield
                label="Salesforce campaign ID"
                formControlName="campaignId"
                ngDefaultControl
                variant="inset"
              >
              </ep-textfield>
            </section>

            <section class="flex flex-col gap-6">
              <app-descriptive-sub-header>
                <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M520-120q-17 0-28.5-11.5T480-160q0-17 11.5-28.5T520-200h240v-560H520q-17 0-28.5-11.5T480-800q0-17 11.5-28.5T520-840h240q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H520Zm-73-320H160q-17 0-28.5-11.5T120-480q0-17 11.5-28.5T160-520h287l-75-75q-11-11-11-27t11-28q11-12 28-12.5t29 11.5l143 143q12 12 12 28t-12 28L429-309q-12 12-28.5 11.5T372-310q-11-12-10.5-28.5T373-366l74-74Z"
                  />
                </svg>
                <ng-container header>Custom Login</ng-container>
                <ng-container>
                  These attributes are used for customizing the login experience for this client.
                </ng-container>
              </app-descriptive-sub-header>
              <app-toggleable-option
                formControlName="hideSocialProviders"
                [appDisablePointerEvents]="!me()?.permissions?.includes('ia_basics')"
              >
                <svg viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M645-555q35-35 35-85t-35-85q-35-35-85-35t-85 35q-35 35-35 85t35 85q35 35 85 35t85-35ZM320-330q45-53 108-81.5T560-440q69 0 132 28.5T800-330v-470H320v470Zm0 90q-33 0-56.5-23.5T240-320v-480q0-33 23.5-56.5T320-880h480q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H320ZM160-80q-33 0-56.5-23.5T80-160v-520q0-17 11.5-28.5T120-720q17 0 28.5 11.5T160-680v520h520q17 0 28.5 11.5T720-120q0 17-11.5 28.5T680-80H160Zm371.5-531.5Q520-623 520-640t11.5-28.5Q543-680 560-680t28.5 11.5Q600-657 600-640t-11.5 28.5Q577-600 560-600t-28.5-11.5ZM428-320h264q-29-20-63-30t-69-10q-35 0-69 10t-63 30Zm132-245Z"
                  />
                </svg>
                <span>Hide Social Providers</span>
                <p ngProjectAs="description">
                  If enabled, social login options will be hidden on the login page for this client.
                  This includes the ebm-papst login as well as others situated at the bottom of the
                  login page. This doesn't stop already logged in users to get through the login
                  flow.
                </p>
              </app-toggleable-option>
            </section>

            <section class="flex flex-col gap-6">
              <app-descriptive-sub-header>
                <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M240-80q-50 0-85-35t-35-85v-40q0-33 23.5-56.5T200-320h40v-480q0-33 23.5-56.5T320-880h440q33 0 56.5 23.5T840-800v600q0 50-35 85t-85 35H240Zm480-80q17 0 28.5-11.5T760-200v-600H320v480h280q33 0 56.5 23.5T680-240v40q0 17 11.5 28.5T720-160ZM400-600q-17 0-28.5-11.5T360-640q0-17 11.5-28.5T400-680h280q17 0 28.5 11.5T720-640q0 17-11.5 28.5T680-600H400Zm0 120q-17 0-28.5-11.5T360-520q0-17 11.5-28.5T400-560h280q17 0 28.5 11.5T720-520q0 17-11.5 28.5T680-480H400ZM240-160h360v-80H200v40q0 17 11.5 28.5T240-160Zm0 0h-40 400-360Z"
                  />
                </svg>
                <ng-container header>Terms and Conditions Localization</ng-container>
                <ng-container>
                  Configure custom terms and conditions text for this client that will be shown to
                  users during login. This allows you to have different terms and conditions for
                  different applications if needed.
                </ng-container>
              </app-descriptive-sub-header>

              @if (!termsEnabled()) {
                <!-- Terms localization not enabled -->
                <div class="flex flex-col gap-3">
                  <p class="body-xs text-fg-muted">
                    No custom terms and conditions localization is configured for this client.
                  </p>
                  <ep-button
                    type="outline"
                    size="small"
                    class="self-start"
                    (click)="enableTermsLocalization()"
                    [disabled]="!me()?.permissions?.includes('ia_terms')"
                  >
                    <svg class="icon icon--small" viewBox="0 -960 960 960" fill="currentColor">
                      <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
                    </svg>
                    <span>Enable Terms Localization</span>
                  </ep-button>
                </div>
              } @else {
                <!-- Terms localization enabled -->
                <div class="flex flex-col gap-4">
                  <div class="flex flex-col gap-1">
                    <label class="body-xs text-fg-muted">Localization Key (auto-generated)</label>
                    <code class="text-sm bg-surface-muted px-2 py-1 rounded self-start">{{
                      termsLocalizationKey()
                    }}</code>
                  </div>

                  <ep-textfield
                    inputType="date"
                    label="Custom terms and conditions update date"
                    formControlName="ebm_specific_terms_date"
                    ngDefaultControl
                    variant="inset"
                  >
                  </ep-textfield>

                  @if (isLoadingTermsMessages()) {
                    <p class="body-xs text-fg-muted">Loading translations...</p>
                  } @else {
                    <!-- Existing locale entries -->
                    @for (entry of termsLocaleEntries(); track entry[0]) {
                      <div
                        class="flex flex-col gap-2 p-4 bg-bg-alt border-l-2 border-stroke-subtle overflow-visible"
                      >
                        <div class="flex items-center justify-between">
                          <label class="font-medium">{{ getLocaleDisplayName(entry[0]) }}</label>
                          <ep-button
                            type="quiet"
                            size="small"
                            (click)="deleteTermsLocale(entry[0])"
                            [disabled]="!me()?.permissions?.includes('ia_terms')"
                          >
                            <svg
                              class="icon icon--small"
                              viewBox="0 -960 960 960"
                              fill="currentColor"
                            >
                              <path
                                d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"
                              />
                            </svg>
                          </ep-button>
                        </div>
                        <app-rich-text-editor
                          class="bg-white"
                          [content]="entry[1]"
                          [placeholder]="
                            me()?.permissions?.includes('ia_terms')
                              ? 'Enter terms text for this locale...'
                              : ''
                          "
                          (contentChange)="updateTermsText(entry[0], $event)"
                          [readOnly]="!me()?.permissions?.includes('ia_terms')"
                        ></app-rich-text-editor>
                      </div>
                    }

                    <!-- Add new locale -->
                    @if (unusedTermsLocales().length > 0) {
                      <div class="flex items-stretch justify-end gap-2">
                        <ep-select class="h-[38px]" #termsLocaleSelect>
                          <ep-option value="">Select a language...</ep-option>
                          @for (locale of unusedTermsLocales(); track locale) {
                            <ep-option [value]="locale">{{
                              getLocaleDisplayName(locale)
                            }}</ep-option>
                          }
                        </ep-select>
                        <ep-button
                          size="small"
                          (click)="addTermsLocale(termsLocaleSelect)"
                          [disabled]="!me()?.permissions?.includes('ia_terms')"
                        >
                          <svg
                            class="icon icon--small"
                            viewBox="0 -960 960 960"
                            fill="currentColor"
                          >
                            <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
                          </svg>
                          <span>Add Language</span>
                        </ep-button>
                      </div>
                    }
                  }

                  <!-- Disable terms localization -->
                  @if (!showDeleteTermsConfirm()) {
                    <div class="flex justify-end">
                      <ep-button
                        variant="quiet"
                        size="small"
                        color="danger"
                        (click)="showDeleteTermsConfirm.set(true)"
                      >
                        <span>Remove All Terms Translations</span>
                      </ep-button>
                    </div>
                  } @else {
                    <div
                      class="flex items-center gap-3 p-3 border-l-2 border-stroke-danger-default bg-bg-danger-default"
                    >
                      <span class="body-xs flex-1 text-fg-danger-emphasis"
                        >This will delete all translations and clear the terms key. Continue?</span
                      >
                      <ep-button
                        size="small"
                        color="danger"
                        (click)="disableTermsLocalization()"
                        [disabled]="isSavingLocalization()"
                      >
                        <span>Confirm</span>
                      </ep-button>
                      <ep-button
                        type="quiet"
                        size="small"
                        (click)="showDeleteTermsConfirm.set(false)"
                      >
                        <span>Cancel</span>
                      </ep-button>
                    </div>
                  }
                </div>
              }
            </section>

            @if (isRealmAdmin()) {
              <ep-accordion class="w-full">
                <ep-accordion-item title="Additional Attributes">
                  <div class="flex flex-col gap-3">
                    <p class="body-xs text-fg-muted">
                      Add custom attributes that are not covered by the fields above. These will be
                      stored in the client's attributes.
                    </p>
                    <div class="flex flex-col gap-2">
                      @for (
                        attribute of customAttributesArray.controls;
                        track attribute;
                        let i = $index
                      ) {
                        <div class="flex items-center gap-2">
                          <div class="flex flex-grow gap-2">
                            <ep-textfield
                              [formControl]="getAttribute(i, 'key')"
                              placeholder="Attribute Key"
                              variant="inset"
                              ngDefaultControl
                              class="w-1/3"
                            >
                            </ep-textfield>
                            <ep-textfield
                              [formControl]="getAttribute(i, 'value')"
                              placeholder="Attribute Value"
                              variant="inset"
                              ngDefaultControl
                              class="flex-grow"
                            >
                            </ep-textfield>
                          </div>
                          <ep-button type="quiet" size="small" (click)="removeCustomAttribute(i)">
                            <svg
                              class="icon icon--small"
                              viewBox="0 -960 960 960"
                              fill="currentColor"
                            >
                              <path
                                d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"
                              />
                            </svg>
                          </ep-button>
                        </div>
                      }
                      <ep-button
                        type="outline"
                        size="small"
                        class="self-start"
                        (click)="addCustomAttribute()"
                      >
                        <svg class="icon icon--small" viewBox="0 -960 960 960" fill="currentColor">
                          <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
                        </svg>
                        <span>Add Attribute</span>
                      </ep-button>
                    </div>
                  </div>
                </ep-accordion-item>
              </ep-accordion>
            }
          </div>
        }

        <!-- Admins Tab -->
        @if (activeTab === 'admins') {
          <!-- Client FGA permissions section -->
          <div class="flex flex-col gap-4">
            <div class="flex items-center justify-between">
              <app-descriptive-sub-header>
                <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M480-240Zm-320 40v-72q0-34 17.5-62.5T224-378q62-31 126-46.5T480-440q37 0 73 4.5t72 14.5l-67 68q-20-3-39-5t-39-2q-56 0-111 13.5T260-306q-9 5-14.5 14t-5.5 20v32h240v80H200q-17 0-28.5-11.5T160-200Zm400 40v-50q0-16 6.5-30.5T584-266l197-197q9-9 20-13t22-4q12 0 23 4.5t20 13.5l37 37q8 9 12.5 20t4.5 22q0 11-4 22.5T903-340L706-143q-11 11-25.5 17t-30.5 6h-50q-17 0-28.5-11.5T560-160Zm300-223-37-37 37 37ZM620-180h38l121-122-18-19-19-18-122 121v38Zm141-141-19-18 37 37-18-19ZM367-527q-47-47-47-113t47-113q47-47 113-47t113 47q47 47 47 113t-47 113q-47 47-113 47t-113-47Zm169.5-56.5Q560-607 560-640t-23.5-56.5Q513-720 480-720t-56.5 23.5Q400-673 400-640t23.5 56.5Q447-560 480-560t56.5-23.5ZM480-640Z"
                  />
                </svg>
                <ng-container header>Client Permissions (FGA)</ng-container>
                <ng-container>
                  Assign permissions to users on a fine-grained level - who can change what in this
                  client
                </ng-container>
              </app-descriptive-sub-header>
              <ep-button
                (click)="openPermissionModal()"
                [disabled]="!me()?.permissions?.includes('ia_full_admin')"
              >
                <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                  <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
                </svg>
                <span>Add User Permission</span>
              </ep-button>
            </div>

            @if (isLoadingPermissions) {
              <div class="text-center py-4">Loading permissions...</div>
            } @else {
              @if (clientPermissions.length === 0) {
                <div class="text-center py-4 text-gray-500">No user permissions added yet</div>
              } @else {
                <div class="overflow-x-auto">
                  <table class="w-full text--small text-fg-muted text-left">
                    <thead class="body-xs text-fg-subtle">
                      <tr>
                        <th class="px-3 py-2">Email</th>
                        <th class="px-3 py-2">Permission</th>
                        <th class="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (perm of clientPermissions; track perm.user + '#' + perm.permission) {
                        <tr class="border-t stroke-subtle">
                          <td class="px-3 py-1">
                            {{ perm.user }}
                          </td>
                          <td class="px-3 py-1" [title]="getPermissionDescription(perm.permission)">
                            {{ getPermissionTitle(perm.permission) }}
                          </td>
                          <td class="px-3 py-1 text-right whitespace-nowrap">
                            <ep-button
                              type="quiet"
                              size="small"
                              color="danger"
                              (click)="removeClientPermission(perm)"
                              title="Remove permission"
                              [disabled]="!me()?.permissions?.includes('ia_full_admin')"
                            >
                              <svg
                                class="icon icon--small"
                                viewBox="0 -960 960 960"
                                fill="currentColor"
                              >
                                <path
                                  d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"
                                />
                              </svg>
                            </ep-button>
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              }
            }
            <!-- Add Permissions Modal -->
            <ep-modal [open]="!!showPermissionModal()" (close)="closePermissionModal()">
              <h3 slot="header">Add User Permission</h3>
              <div class="flex flex-col gap-4 min-w-[300px]">
                <ep-combobox
                  dynamic="true"
                  manualDynamic="true"
                  searchDelay="300"
                  placeholder="Search users by name or email..."
                  size="medium"
                  (search)="searchUsers($event)"
                  (change)="onUserSelected($event)"
                >
                  <label slot="label">Select User</label>
                  @for (user of userSearchResults(); track user.id) {
                    <ep-option [value]="user.email" [label]="getUserDisplayName(user)">
                      <span slot="text">{{ getUserDisplayName(user) }}</span>
                      <span slot="subtext">{{ user.email || 'No email' }}</span>
                    </ep-option>
                  }
                </ep-combobox>
                <ep-combobox size="medium" (change)="onPermissionSelected($event)">
                  <label slot="label">Select permission</label>
                  @for (perm of iaPrimaryPermissions; track perm) {
                    <ep-option [value]="perm" [label]="getPermissionTitle(perm)">
                      <span slot="text">{{ getPermissionTitle(perm) }}</span>
                      <span slot="subtext">{{ getPermissionDescription(perm) }}</span>
                    </ep-option>
                  }
                  <ep-divider></ep-divider>
                  <ep-list-title>Specific per tab permissions</ep-list-title>
                  @for (perm of iaSecondaryPermissions; track perm) {
                    <ep-option [value]="perm" [label]="getPermissionTitle(perm)">
                      <span slot="text">{{ getPermissionTitle(perm) }}</span>
                      <span slot="subtext">{{ getPermissionDescription(perm) }}</span>
                    </ep-option>
                  }
                </ep-combobox>
              </div>
              <div class="flex justify-end gap-2" slot="actions">
                <ep-button (click)="closePermissionModal()">Cancel</ep-button>
                <ep-button
                  color="primary"
                  (click)="addClientPermission()"
                  [disabled]="!selectedUserEmail() || !selectedPermission() || isLoadingPermissions"
                >
                  <span>Add</span>
                </ep-button>
              </div>
            </ep-modal>
          </div>
        }

        <!-- Deny access Tab -->
        @if (activeTab === 'deny-access') {
          <div class="flex flex-col gap-6" formGroupName="denyAccess">
            <h3>Deny Access Settings</h3>

            <app-toggleable-option
              formControlName="enabled"
              [appDisablePointerEvents]="!me()?.permissions?.includes('ia_denyconfig')"
            >
              <svg viewBox="0 -960 960 960" fill="currentColor">
                <path
                  d="M320-440h320q17 0 28.5-11.5T680-480q0-17-11.5-28.5T640-520H320q-17 0-28.5 11.5T280-480q0 17 11.5 28.5T320-440ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"
                />
              </svg>
              <span>Enable Deny Access</span>
              <p ngProjectAs="description">
                When enabled, this client will deny access to users based on the criteria defined
                below. This is useful for restricting access to certain clients for specific user
                groups or types.
              </p>
            </app-toggleable-option>

            @if (clientForm.get('denyAccess.enabled')?.value) {
              <div class="divider"></div>
              <div class="flex flex-col gap-5">
                <section class="flex flex-col gap-5">
                  <app-descriptive-sub-header>
                    <svg viewBox="0 -960 960 960" fill="currentColor">
                      <path
                        d="M702-480 560-622l57-56 85 85 170-170 56 57-226 226Zm-455-47q-47-47-47-113t47-113q47-47 113-47t113 47q47 47 47 113t-47 113q-47 47-113 47t-113-47ZM40-160v-112q0-34 17.5-62.5T104-378q62-31 126-46.5T360-440q66 0 130 15.5T616-378q29 15 46.5 43.5T680-272v112H40Zm80-80h480v-32q0-11-5.5-20T580-306q-54-27-109-40.5T360-360q-56 0-111 13.5T140-306q-9 5-14.5 14t-5.5 20v32Zm296.5-343.5Q440-607 440-640t-23.5-56.5Q393-720 360-720t-56.5 23.5Q280-673 280-640t23.5 56.5Q327-560 360-560t56.5-23.5ZM360-300Zm0-340Z"
                      />
                    </svg>
                    <ng-container header>User type</ng-container>

                    <ng-container>Restrict access based on user type</ng-container>
                  </app-descriptive-sub-header>

                  <div class="field-wrapper">
                    <label for="userTypeSelect">User Type Requirement</label>
                    <ep-select
                      id="userTypeSelect"
                      formControlName="userType"
                      placeholder="Allow all user types"
                      ngDefaultControl
                      variant="inset"
                    >
                      <ep-option value="" label="All users">
                        <span slot="text">All users</span>
                      </ep-option>
                      <ep-option value="employee" label="Employee">
                        <span slot="text">Employee</span>
                      </ep-option>
                      <ep-option value="customer" label="Customer">
                        <span slot="text">Customer</span>
                      </ep-option>
                    </ep-select>
                    <span class="hints">Restrict access based on user type</span>
                  </div>
                </section>

                <div class="divider"></div>

                <section class="flex flex-col gap-5">
                  <app-descriptive-sub-header>
                    <svg viewBox="0 -960 960 960" fill="currentColor">
                      <path
                        d="M480-80q-139-35-229.5-159.5T160-516v-244l320-120 320 120v244q0 152-90.5 276.5T480-80Zm0-84q104-33 172-132t68-220v-189l-240-90-240 90v189q0 121 68 220t172 132Zm0-316Zm-80 160h160q17 0 28.5-11.5T600-360v-120q0-17-11.5-28.5T560-520v-40q0-33-23.5-56.5T480-640q-33 0-56.5 23.5T400-560v40q-17 0-28.5 11.5T360-480v120q0 17 11.5 28.5T400-320Zm40-200v-40q0-17 11.5-28.5T480-600q17 0 28.5 11.5T520-560v40h-80Z"
                      />
                    </svg>
                    <ng-container header>Role</ng-container>
                    <ng-container>
                      Restrict access to users with specific roles of this client
                    </ng-container>
                  </app-descriptive-sub-header>

                  <div class="field-wrapper">
                    <label for="anyClientRoleSelect">Users with any role of this client</label>
                    <ep-select
                      id="anyClientRoleSelect"
                      formControlName="anyClientRole"
                      placeholder="No restriction"
                      ngDefaultControl
                      #anyClientRoleSelect
                      variant="inset"
                    >
                      <ep-option value="" label="No restriction">
                        <span slot="text">No restriction</span>
                      </ep-option>
                      <ep-option
                        value="true"
                        label="Allow all users with at least any role of this client"
                      >
                        <span slot="text"
                          >Allow all users with at least any role of this client</span
                        >
                      </ep-option>
                    </ep-select>

                    <span class="textfield__hints"
                      >User is allowed if they have at least one of the roles of this client, no
                      matter which one</span
                    >
                  </div>

                  @if (clientForm.get('denyAccess.anyClientRole')?.value !== 'true') {
                    <div class="flex flex-col gap-3">
                      <ep-combobox
                        label="Require specific client roles"
                        placeholder="Search for required roles..."
                        size="medium"
                        hint="User must have at least one of the selected roles (JSON array of role IDs)"
                        multiple
                        formControlName="roles"
                        ngDefaultControl
                        variant="inset"
                      >
                        @for (role of clientRoles; track role.id) {
                          <ep-option [value]="role.name" [label]="role.name">
                            <span slot="text">{{ role.name }}</span>
                          </ep-option>
                        }
                      </ep-combobox>

                      <div class="field-wrapper">
                        <label for="rolesModeSelect">Roles Matching Mode</label>
                        <ep-select
                          id="rolesModeSelect"
                          formControlName="rolesMode"
                          hint="How to match required roles"
                          placeholder="Any (at least one role)"
                          ngDefaultControl
                          variant="inset"
                        >
                          <ep-option value="ANY" label="Any (at least one role)">
                            <span slot="text">Any (at least one role)</span>
                          </ep-option>
                          <ep-option value="ALL" label="All (all roles required)">
                            <span slot="text">All (all roles required)</span>
                          </ep-option>
                          <ep-option value="DENY" label="Deny (deny if has any role)">
                            <span slot="text">Deny (deny if has any role)</span>
                          </ep-option>
                        </ep-select>
                      </div>
                    </div>
                  } @else {
                    <div>
                      <ep-alert color="info" variant="alt">
                        <svg
                          class="icon"
                          slot="prefix"
                          viewBox="0 -960 960 960"
                          fill="currentColor"
                        >
                          <path
                            d="M109-120q-11 0-20-5.5T75-140q-5-9-5.5-19.5T75-180l370-640q6-10 15.5-15t19.5-5q10 0 19.5 5t15.5 15l370 640q6 10 5.5 20.5T885-140q-5 9-14 14.5t-20 5.5H109Zm69-80h604L480-720 178-200Zm330.5-51.5Q520-263 520-280t-11.5-28.5Q497-320 480-320t-28.5 11.5Q440-297 440-280t11.5 28.5Q463-240 480-240t28.5-11.5Zm0-120Q520-383 520-400v-120q0-17-11.5-28.5T480-560q-17 0-28.5 11.5T440-520v120q0 17 11.5 28.5T480-360q17 0 28.5-11.5ZM480-460Z"
                          />
                        </svg>
                        <span slot="header">Any Role Restriction Enabled</span>
                        <p slot="subtext">
                          Access is restricted to users with at least any role of this client. The
                          specific roles assigned to the user do not matter, as long as they have at
                          least one role from this client.
                        </p>
                      </ep-alert>
                    </div>
                  }
                </section>

                <div class="divider"></div>

                <section class="flex flex-col gap-5">
                  <app-descriptive-sub-header>
                    <svg viewBox="0 -960 960 960" fill="currentColor">
                      <path
                        d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-7-.5-14.5T799-507q-5 29-27 48t-52 19h-80q-33 0-56.5-23.5T560-520v-40H400v-80q0-33 23.5-56.5T480-720h40q0-23 12.5-40.5T563-789q-20-5-40.5-8t-42.5-3q-134 0-227 93t-93 227h200q66 0 113 47t47 113v40H400v110q20 5 39.5 7.5T480-160Z"
                      />
                    </svg>
                    <ng-container header>Countries</ng-container>
                    <ng-container>
                      Restrict access to users from specific countries based on their employee or
                      customer information. User must be from at least one of the selected countries
                      to access the client.
                    </ng-container>
                  </app-descriptive-sub-header>

                  <ep-combobox
                    label="Required countries"
                    placeholder="Search for required countries..."
                    size="medium"
                    hint="User must be from at least one of the selected countries"
                    multiple
                    formControlName="countries"
                    ngDefaultControl
                    variant="inset"
                  >
                    @for (country of localizedCountryCodes(); track country.code) {
                      <ep-option [value]="country.code" [label]="country.name">
                        <span slot="text">{{ country.name }}</span>
                      </ep-option>
                    }
                  </ep-combobox>
                </section>

                @if (isRealmAdmin()) {
                  <div class="divider"></div>
                  <section class="flex flex-col gap-5">
                    <app-descriptive-sub-header>
                      <svg viewBox="0 -960 960 960" fill="currentColor">
                        <path
                          d="M603.5-193.5Q560-237 560-300t43.5-106.5Q647-450 710-450t106.5 43.5Q860-363 860-300t-43.5 106.5Q773-150 710-150t-106.5-43.5Zm156-57Q780-271 780-300t-20.5-49.5Q739-370 710-370t-49.5 20.5Q640-329 640-300t20.5 49.5Q681-230 710-230t49.5-20.5ZM160-260v-80h320v80H160Zm-16.5-293.5Q100-597 100-660t43.5-106.5Q187-810 250-810t106.5 43.5Q400-723 400-660t-43.5 106.5Q313-510 250-510t-106.5-43.5Zm156-57Q320-631 320-660t-20.5-49.5Q279-730 250-730t-49.5 20.5Q180-689 180-660t20.5 49.5Q221-590 250-590t49.5-20.5ZM480-620v-80h320v80H480Zm230 320ZM250-660Z"
                        />
                      </svg>
                      <ng-container header
                        >Custom Attribute Filters (Realm Admin Only)</ng-container
                      >
                      <ng-container>
                        Define custom user attribute regex filters. Each entry requires an attribute
                        name and a regex pattern.
                      </ng-container>
                    </app-descriptive-sub-header>

                    <div class="flex flex-col gap-3">
                      @for (
                        filter of customAttributeFiltersArray.controls;
                        track filter;
                        let i = $index
                      ) {
                        <div class="flex items-center gap-2">
                          <div class="flex flex-grow gap-2">
                            <ep-textfield
                              label="Attribute Name"
                              [value]="getCustomAttributeFilter(i, 'attributeName').value"
                              (input)="
                                getCustomAttributeFilter(i, 'attributeName').setValue(
                                  $any($event.target).value
                                )
                              "
                              placeholder="e.g. department"
                              class="flex-1"
                              variant="inset"
                            >
                            </ep-textfield>
                            <ep-textfield
                              label="Regex Pattern"
                              [value]="getCustomAttributeFilter(i, 'regex').value"
                              (input)="
                                getCustomAttributeFilter(i, 'regex').setValue(
                                  $any($event.target).value
                                )
                              "
                              placeholder="e.g. ^IT$"
                              hint="Regular expression to match attribute value"
                              class="flex-1"
                              variant="inset"
                            >
                            </ep-textfield>
                          </div>
                          <ep-button
                            size="small"
                            type="secondary"
                            (click)="removeCustomAttributeFilter(i)"
                            [disabled]="customAttributeFiltersArray.length === 1"
                          >
                            <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                              <path
                                d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"
                              />
                            </svg>
                            <span>Remove</span>
                          </ep-button>
                        </div>
                      }
                      <div class="flex justify-end">
                        <ep-button
                          size="small"
                          type="secondary"
                          class="self-start"
                          (click)="addCustomAttributeFilter()"
                        >
                          <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                            <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
                          </svg>
                          <span>Add Filter</span>
                        </ep-button>
                      </div>
                    </div>
                  </section>
                }

                <div class="divider"></div>

                <section class="flex flex-col gap-5">
                  <app-descriptive-sub-header>
                    <svg viewBox="0 -960 960 960" fill="currentColor">
                      <path
                        d="M280-440h400v-80H280v80ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"
                      />
                    </svg>
                    <ng-container header>Custom denial behaviour</ng-container>
                    <ng-container>
                      Configure a custom deny message to show to users who are denied access if
                      needed. This allows you to provide a localized message explaining why access
                      was denied and what users can do next.
                    </ng-container>
                  </app-descriptive-sub-header>

                  @if (!denyMessageEnabled()) {
                    <!-- Deny message localization not enabled -->
                    <div class="flex flex-col gap-3">
                      <p class="body-xs text-fg-muted">
                        No custom deny message localization is configured for this client. It will
                        show a generic access denied message to users who are denied access.
                      </p>

                      <div class="flex justify-end">
                        <ep-button
                          type="outline"
                          size="small"
                          (click)="enableDenyMessageLocalization()"
                          [disabled]="!me()?.permissions?.includes('ia_denyconfig')"
                        >
                          <svg
                            class="icon icon--small"
                            viewBox="0 -960 960 960"
                            fill="currentColor"
                          >
                            <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
                          </svg>
                          <span>Enable Deny Message Localization</span>
                        </ep-button>
                      </div>
                    </div>
                  } @else {
                    <!-- Deny message localization enabled -->
                    <div class="flex flex-col gap-4">
                      <div class="flex flex-col gap-1">
                        <label class="body-xs text-fg-muted"
                          >Localization Key (auto-generated)</label
                        >
                        <code class="text-sm bg-surface-muted px-2 py-1 rounded self-start">{{
                          denyLocalizationKey()
                        }}</code>
                      </div>

                      @if (isLoadingDenyMessages()) {
                        <p class="body-xs text-fg-muted">Loading translations...</p>
                      } @else {
                        <!-- Existing locale entries -->
                        @for (entry of denyLocaleEntries(); track entry[0]) {
                          <div
                            class="flex flex-col gap-2 p-4 bg-bg-alt border-l-2 border-stroke-subtle overflow-visible"
                          >
                            <div class="flex items-center justify-between">
                              <label class="font-medium">{{
                                getLocaleDisplayName(entry[0])
                              }}</label>
                              <ep-button
                                type="quiet"
                                size="small"
                                (click)="deleteDenyLocale(entry[0])"
                                [disabled]="!me()?.permissions?.includes('ia_denyconfig')"
                              >
                                <svg
                                  class="icon icon--small"
                                  viewBox="0 -960 960 960"
                                  fill="currentColor"
                                >
                                  <path
                                    d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"
                                  />
                                </svg>
                              </ep-button>
                            </div>
                            <app-rich-text-editor
                              class="bg-white"
                              [content]="entry[1]"
                              placeholder="Enter deny message for this locale..."
                              (contentChange)="updateDenyText(entry[0], $event)"
                              [readOnly]="!me()?.permissions?.includes('ia_denyconfig')"
                            ></app-rich-text-editor>
                          </div>
                        }

                        <!-- Add new locale -->
                        @if (unusedDenyLocales().length > 0) {
                          <div class="flex items-stretch justify-end gap-2">
                            <ep-select class="h-[38px]" #denyLocaleSelect>
                              <ep-option value="">Select a language...</ep-option>
                              @for (locale of unusedDenyLocales(); track locale) {
                                <ep-option [value]="locale">{{
                                  getLocaleDisplayName(locale)
                                }}</ep-option>
                              }
                            </ep-select>
                            <ep-button
                              type="outline"
                              size="small"
                              (click)="addDenyLocale(denyLocaleSelect)"
                              [disabled]="!me()?.permissions?.includes('ia_denyconfig')"
                            >
                              <svg
                                class="icon icon--small"
                                viewBox="0 -960 960 960"
                                fill="currentColor"
                              >
                                <path
                                  d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z"
                                />
                              </svg>
                              <span>Add Language</span>
                            </ep-button>
                          </div>
                        }
                      }

                      <!-- Disable deny message localization -->
                      @if (!showDeleteDenyConfirm()) {
                        <div class="flex justify-end">
                          <ep-button
                            variant="quiet"
                            size="small"
                            color="danger"
                            (click)="showDeleteDenyConfirm.set(true)"
                            [disabled]="!me()?.permissions?.includes('ia_denyconfig')"
                          >
                            <span>Remove All Deny Message Translations</span>
                          </ep-button>
                        </div>
                      } @else {
                        <div
                          class="flex items-center gap-3 p-3 border-l-2 border-danger bg-bg-danger-default text-fg-danger-default"
                        >
                          <span class="body-xs flex-1"
                            >This will delete all translations and clear the deny message key.
                            Continue?</span
                          >
                          <ep-button
                            size="small"
                            color="danger"
                            (click)="disableDenyMessageLocalization()"
                            [disabled]="
                              isSavingLocalization() ||
                              !me()?.permissions?.includes('ia_denyconfig')
                            "
                          >
                            <span>Confirm</span>
                          </ep-button>
                          <ep-button
                            type="quiet"
                            size="small"
                            (click)="showDeleteDenyConfirm.set(false)"
                            [disabled]="!me()?.permissions?.includes('ia_denyconfig')"
                          >
                            <span>Cancel</span>
                          </ep-button>
                        </div>
                      }
                    </div>
                  }

                  <ep-textfield
                    label="Denial Redirect URI"
                    formControlName="redirectUri"
                    hint="URL to redirect users when access is denied"
                    ngDefaultControl
                    variant="inset"
                    inputType="url"
                  >
                  </ep-textfield>
                </section>
              </div>
            }
          </div>
        }

        <div class="flex justify-end items-center gap-4">
          <ep-button type="button" (click)="loadClientData()" [disabled]="isSaving">
            <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
              <path
                d="M480-160q-134 0-227-93t-93-227q0-134 93-227t227-93q69 0 132 28.5T720-690v-110h80v280H520v-80h168q-32-56-87.5-88T480-720q-100 0-170 70t-70 170q0 100 70 170t170 70q77 0 139-44t87-116h84q-28 106-114 173t-196 67Z"
              />
            </svg>
            <span>Refresh</span>
          </ep-button>

          <ep-button
            color="primary"
            (click)="saveClientAttributes()"
            [disabled]="isSaving || !me()?.permissions?.length"
          >
            <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
              <path
                d="M840-680v480q0 33-23.5 56.5T760-120H200q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h480l160 160Zm-80 34L646-760H200v560h560v-446ZM480-240q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35ZM240-560h360v-160H240v160Zm-40-86v446-560 114Z"
              />
            </svg>
            <span>Save Changes</span>
          </ep-button>
        </div>
      </form>
    </section>

    @if (successMessage) {
      <ep-alert type="toast" variant="alt" color="success" [center]="true" [bottom]="true">
        <span slot="header">{{ successMessage }}</span>
      </ep-alert>
    }
    @if (errorMessage) {
      <ep-alert type="toast" variant="alt" color="danger" [center]="true" [bottom]="true">
        <span slot="header">{{ errorMessage }}</span>
      </ep-alert>
    }
  }
</div>
